// src/components/Settings.js
import React, { useEffect, useCallback, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { updateUserSettings, fetchUserSettings } from "../store/settingsSlice";

const DEFAULT_HIGHLIGHT_COLORS = ["#fef08a", "#bbf7d0", "#e9d5ff"];
const HIGHLIGHT_PRESET_LABELS = ["Yellow", "Mint", "Lavender"];

const Settings = () => {
  const dispatch = useDispatch();
  const userSettings = useSelector((state) => state.settings);

  const getHighlightColors = (settings) => {
    if (Array.isArray(settings.highlightColors) && settings.highlightColors.length >= 3) {
      return settings.highlightColors.slice(0, 3);
    }
    if (settings.highlightColor) {
      return [settings.highlightColor, DEFAULT_HIGHLIGHT_COLORS[1], DEFAULT_HIGHLIGHT_COLORS[2]];
    }
    return DEFAULT_HIGHLIGHT_COLORS;
  };

  // Initialize react-hook-form with default values including defaultLanguage.
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    formState: { isDirty, errors },
  } = useForm({
      defaultValues: {
        defaultVersion: userSettings.defaultVersion || "ESV",
        highlightColors: getHighlightColors(userSettings),
        defaultLanguage: userSettings.defaultLanguage || "en",
        showInterlinear: Boolean(userSettings.showInterlinear),
      },
  });

  const selectedColors = watch("highlightColors") || DEFAULT_HIGHLIGHT_COLORS;

  // When userSettings change, update the form values.
  useEffect(() => {
    reset({
      defaultVersion: userSettings.defaultVersion || "ESV",
      highlightColors: getHighlightColors(userSettings),
      defaultLanguage: userSettings.defaultLanguage || "en",
      showInterlinear: Boolean(userSettings.showInterlinear),
    });
  }, [userSettings, reset]);

  // Fetch user settings if not loaded.
  useEffect(() => {
    if (!userSettings.isLoaded) {
      dispatch(fetchUserSettings());
    }
  }, [dispatch, userSettings.isLoaded]);

  const [notification, setNotification] = useState("");

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        highlightColor: data.highlightColors?.[0] || DEFAULT_HIGHLIGHT_COLORS[0],
      };
      const resultAction = await dispatch(updateUserSettings(payload));
      if (updateUserSettings.fulfilled.match(resultAction)) {
        // Update default language immediately.
        localStorage.setItem("language", data.defaultLanguage);
        window.location.reload();
        // Reset the form so the UI reflects the new settings.
        reset(payload);
        // Use a timeout to allow React to flush UI changes before showing the notification.
        setTimeout(() => {
          setNotification("Settings saved successfully!");
          setTimeout(() => setNotification(""), 3000);
        }, 0);
      } else {
        setNotification("Failed to save settings. Please try again.");
        setTimeout(() => setNotification(""), 3000);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setNotification("An error occurred. Please try again.");
      setTimeout(() => setNotification(""), 3000);
    }
  };

  // Warn user about unsaved changes before unload.
  const handleBeforeUnload = useCallback(
    (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    [isDirty]
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [handleBeforeUnload]);

  const handleResetColor = () => {
    reset({ ...getValues(), highlightColors: DEFAULT_HIGHLIGHT_COLORS });
  };

  return (
    <div className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-lg space-y-8">
      {notification && (
        <div className="p-4 mb-4 text-center text-white bg-pink-300 rounded-md">
          {notification}
        </div>
      )}

      <h1 className="text-xl font-bold text-gray-800 mb-4">Settings</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Default Version Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Default Version</h2>
          <label htmlFor="defaultVersion" className="block text-gray-700">
            Select your default Bible version:
          </label>
          <Controller
            name="defaultVersion"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <select
                id="defaultVersion"
                {...field}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="KRV">Korean Revised Version (KRV)</option>
                <option value="SAE">새번역 (SAE)</option>
                <option value="ESV">English Standard Version (ESV)</option>
                <option value="NIV">New International Version (NIV)</option>
                <option value="NLT">New Living Translation (NLT)</option>
              </select>
            )}
          />
          {errors.defaultVersion && (
            <p className="text-red-500 text-sm">Default version is required.</p>
          )}
        </div>

        {/* Default Language Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Default Language</h2>
          <label htmlFor="defaultLanguage" className="block text-gray-700">
            Select your default language:
          </label>
          <Controller
            name="defaultLanguage"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <select
                id="defaultLanguage"
                {...field}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="en">English</option>
                <option value="ko">한국어</option>
              </select>
            )}
          />
          {errors.defaultLanguage && (
            <p className="text-red-500 text-sm">Default language is required.</p>
          )}
        </div>

        {/* Highlight Color Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Highlight Colors</h2>
          <p className="text-gray-700 mb-3">
            Customize the three default highlight swatches shown in the reader.
          </p>
          <div className="space-y-4">
            {HIGHLIGHT_PRESET_LABELS.map((label, index) => (
              <div key={label} className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-800">{label}</span>
                  <span
                    className="px-3 py-1 rounded-md text-sm text-gray-800"
                    style={{ backgroundColor: selectedColors[index] || DEFAULT_HIGHLIGHT_COLORS[index] }}
                  >
                    Preview
                  </span>
                </div>
                <Controller
                  name={`highlightColors.${index}`}
                  control={control}
                  rules={{
                    required: true,
                    pattern: /^#[0-9A-Fa-f]{6}$/,
                  }}
                  render={({ field }) => (
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        {...field}
                        className="p-1 w-16 h-10 cursor-pointer border-none rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        aria-label={`Select ${label} highlight color`}
                      />
                      <input
                        type="text"
                        {...field}
                        className="p-2 border rounded-md w-28 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="#RRGGBB"
                      />
                    </div>
                  )}
                />
                {errors.highlightColors?.[index] && (
                  <p className="text-red-500 text-sm mt-2">
                    Please enter a valid hex color code for {label.toLowerCase()}.
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleResetColor}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-300"
            >
              Reset to Default
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Interlinear</h2>
          <label className="flex items-center gap-3 text-gray-700">
            <Controller
              name="showInterlinear"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={Boolean(field.value)}
                  onChange={(event) => field.onChange(event.target.checked)}
                  className="h-4 w-4"
                />
              )}
            />
            Show ESV interlinear lines in the reader
          </label>
          <p className="text-sm text-gray-500 mt-2">
            This only appears for ESV verses that have interlinear data.
          </p>
        </div>

        {/* Save Settings Button */}
        <button
          type="submit"
          className="w-full bg-rose-800 text-white font-bold py-2 px-4 rounded hover:bg-blue-900 transition duration-300"
        >
          Save Settings
        </button>
      </form>
    </div>
  );
};

export default Settings;
